import { supabase } from "./client";

/**
 * Call the website-proxy Edge Function.
 * Extracts the actual error body from FunctionsHttpError (supabase-js
 * wraps non-2xx responses as generic errors — the real message is in
 * error.context).
 */
export async function websiteProxy<T>(
  action: string,
  params?: Record<string, unknown>
): Promise<{ data: T | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke("website-proxy", {
    body: { action, ...params },
  });

  if (error) {
    // supabase-js wraps non-2xx as FunctionsHttpError — extract actual body
    let detail = error.message || "Edge function error";
    if (error.context && typeof error.context.json === "function") {
      try {
        const errBody = await error.context.json();
        detail = errBody?.error || JSON.stringify(errBody);
      } catch {
        /* ignore */
      }
    }
    return { data: null, error: detail };
  }

  return { data: data as T, error: null };
}

// --- Driver Applications ---

export interface DriverApplication {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  region: string;
  address: string;
  residence_duration: string;
  ownership_status: string;
  education_level: string;
  employment_status: string;
  employer_name: string | null;
  years_with_employer: string | null;
  license_number: string | null;
  license_class: string;
  years_experience: string;
  ride_hailing_experience: string | null;
  deposit_amount: string;
  start_date: string;
  status: string;
  notes: string | null;
  contacted_at: string | null;
  created_at: string;
}

/** Pipeline groups for driver applications. The three status groups are scoped
 *  to Greater Accra; "other_regions" holds every application from anywhere else. */
export type DriverApplicationGroup = 'new' | 'noted' | 'contacted' | 'other_regions';

export interface DriverApplicationCounts {
  all: number;
  new: number;
  noted: number;
  contacted: number;
  other_regions: number;
}

export async function listDriverApplications(page = 1, group?: DriverApplicationGroup) {
  return websiteProxy<{ data: DriverApplication[]; count: number; pageSize: number }>(
    "list-driver-applications",
    { page, ...(group && { group }) }
  );
}

export async function getDriverApplicationCounts() {
  return websiteProxy<{ counts: DriverApplicationCounts }>(
    "driver-application-counts"
  );
}

export async function markDriverApplicationContacted(id: number) {
  return websiteProxy<{ data: DriverApplication }>(
    "mark-contacted",
    { id }
  );
}

export async function undoDriverApplicationContacted(id: number) {
  return websiteProxy<{ data: DriverApplication }>(
    "undo-contacted",
    { id }
  );
}

export async function addDriverApplicationNote(id: number, note: string) {
  return websiteProxy<{ data: DriverApplication }>(
    "add-driver-application-note",
    { id, note }
  );
}

// --- Partner Applications ---

export interface PartnerApplication {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  entity_type: string;
  location: string;
  partnership_type: string;
  has_experience: string;
  vehicle_count_range: string;
  vehicle_type: string | null;
  buy_start_time: string | null;
  referral: string | null;
  main_goal: string;
  comfort_performance: string;
  additional_info: string | null;
  notes: string | null;
  contacted_at: string | null;
  status: string;
  created_at: string;
}

export async function listPartnerApplications(page = 1, status?: string) {
  return websiteProxy<{ data: PartnerApplication[]; count: number }>(
    "list-partner-applications",
    { page, ...(status && { status }) }
  );
}

export async function markPartnerApplicationContacted(id: number) {
  return websiteProxy<{ data: PartnerApplication }>(
    "mark-partner-contacted",
    { id }
  );
}

export async function undoPartnerApplicationContacted(id: number) {
  return websiteProxy<{ data: PartnerApplication }>(
    "undo-partner-contacted",
    { id }
  );
}

export async function addPartnerApplicationNote(id: number, note: string) {
  return websiteProxy<{ data: PartnerApplication }>(
    "add-partner-application-note",
    { id, note }
  );
}
